import { useState, useEffect } from 'react';

const STORAGE_KEY = 'imageViewerOnboardingSeen';

/**
 * Hook to manage mobile onboarding overlay state
 * Shows swipe hints on first visit for touch devices
 * 
 * @param {boolean} isTouchDevice - Whether the device is touch-primary
 * @param {boolean} isEntered - Whether the viewer has completed entrance animation
 * @returns {boolean} Whether to show the onboarding overlay
 */
export const useOnboarding = (isTouchDevice, isEntered) => {
    // Starts false to match SSR output
    const [showOnboarding, setShowOnboarding] = useState(false);
    
    // Show onboarding overlay on mobile first-time only
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!isTouchDevice || !isEntered) return;
        
        const hasSeenOnboarding = localStorage.getItem(STORAGE_KEY);
        
        if (!hasSeenOnboarding) {
            // Show overlay after a short delay to let image render
            const showTimer = setTimeout(() => {
                setShowOnboarding(true);
                // Mark as seen immediately (even if user closes before timer)
                localStorage.setItem(STORAGE_KEY, 'true');
            }, 300);
            
            return () => clearTimeout(showTimer);
        }
    }, [isTouchDevice, isEntered]);
    
    // Auto-hide onboarding after animation completes, and hide immediately on any touch
    useEffect(() => {
        if (!showOnboarding) return;
        
        // Auto-hide after 1.5s (matches CSS animation duration)
        const hideTimer = setTimeout(() => {
            setShowOnboarding(false);
        }, 1500);
        
        // Hide immediately on any touch
        const hideOnTouch = () => {
            setShowOnboarding(false);
        };
        
        document.addEventListener('touchstart', hideOnTouch, { once: true });
        
        return () => {
            clearTimeout(hideTimer);
            document.removeEventListener('touchstart', hideOnTouch);
        };
    }, [showOnboarding]);
    
    return showOnboarding;
};

export default useOnboarding;
